import { useBillingStore } from '@/stores/billing';
import { useBillingUtils } from '@/use/billingUtils';
import { computed } from 'vue';

export type PauseUnavailableReason = 'pendingCancel' | 'cooldown' | 'yearly';

/**
 * The one owner of why pausing is unavailable, shared by the portal's Pause item
 * (tooltip + click toast) and the cancel survey's notice, so their precedence
 * cannot drift. Cooldown outranks yearly: switching to monthly wouldn't unlock
 * pausing, so leading with the yearly advice sends the customer to change their
 * plan for nothing.
 *
 * `pendingCancel` outranks both. Pausing clears cancel_at_period_end server-side, so
 * offering it to someone who has already cancelled is offering to silently un-cancel
 * them — and the cancel they make next reads as paused and goes terminal, forfeiting
 * the period they already paid for (DAB-1288). The other two reasons are "not right
 * now"; this one is "not without undoing a decision you already made", so it leads.
 * The server refuses this independently (409 PAUSE_PENDING_CANCEL) — this is the
 * explanation, not the enforcement.
 */
export function usePauseAvailability() {
  const billingStore = useBillingStore();
  const { formatUnixDate } = useBillingUtils();

  const isYearlyPlan = computed(() => billingStore.primarySubscription?.plan?.interval === 'year');
  // A trialing sub is *created* with cancel_at_period_end (DAB-1001), where the flag marks an
  // unconverted trial rather than a cancellation — same carve-out the server makes.
  const hasPendingCancel = computed(
    () =>
      Boolean(billingStore.primarySubscription?.cancel_at_period_end) &&
      billingStore.primarySubscription?.status !== 'trialing',
  );
  const reason = computed<PauseUnavailableReason | null>(() =>
    hasPendingCancel.value
      ? 'pendingCancel'
      : billingStore.isPauseCoolingDown
        ? 'cooldown'
        : isYearlyPlan.value
          ? 'yearly'
          : null,
  );
  const cooldownDateFormatted = computed(() => formatUnixDate(billingStore.pauseCooldownUntil));

  return { reason, cooldownDateFormatted, isYearlyPlan };
}
